import { useState } from 'react'
import { Button } from '@heroui/react'
import { ChevronDown, Star } from 'lucide-react'
import { usePublicCategories } from '~/features/categories/hooks/use-public-categories'
import { useLocale, useTranslations } from 'use-intl'
import { useQuery } from '@tanstack/react-query'
import { createSessionQuery } from '~/lib/session-query'
import { useAuthModalStore } from '~/stores/use-auth-modal-store'

interface CategoryChipsProps {
  selectedId?: string
  onSelect?: (id: string) => void
  favoritesOnly?: boolean
  onToggleFavorites?: (val: boolean) => void
  compact?: boolean
}

/** Number of category chips to show before collapsing */
const COLLAPSED_LIMIT = 6

export function CategoryChips({ selectedId, onSelect, favoritesOnly, onToggleFavorites, compact = false }: Readonly<CategoryChipsProps>) {
  const locale = useLocale()
  const t = useTranslations('discovery')
  const { data: categoriesResponse } = usePublicCategories()
  const categories = Array.isArray(categoriesResponse) ? categoriesResponse : ((categoriesResponse as any)?.data || [])
  const { data: session } = useQuery(createSessionQuery())
  const openAuthModal = useAuthModalStore(s => s.openAuthModal)
  const [isExpanded, setIsExpanded] = useState(false)

  const handleFavoritesClick = () => {
    if (!session?.user) {
      openAuthModal()
      return
    }
    onToggleFavorites?.(!favoritesOnly)
  }

  const getCategoryName = (c: any) => {
    const langMap: Record<string, string> = { en: c.en, 'zh-CN': c.cn, 'zh-TW': c.tw, ko: c.kr }
    return langMap[locale] || c.name || ''
  }

  const hasOverflow = categories.length > COLLAPSED_LIMIT
  const visibleCategories = isExpanded ? categories : categories.slice(0, COLLAPSED_LIMIT)

  return (
    <div className={`w-full py-3 ${compact ? 'px-0 bg-transparent' : 'px-4 bg-gradient-to-b from-white/80 to-transparent backdrop-blur-[2px]'} z-40`}>
      <div className={`${compact ? 'flex-wrap' : 'overflow-x-auto no-scrollbar'} flex gap-2`}>
        <Button
          size="sm"
          variant={favoritesOnly ? 'primary' : 'ghost'}
          onPress={handleFavoritesClick}
          aria-label={t('saved')}
          className={`min-h-11 shrink-0 rounded-full px-4 py-1.5 text-xs font-medium flex items-center gap-1.5 transition-colors
            ${favoritesOnly ? 'bg-primary text-primary-foreground' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}
          `}
        >
          <Star className={`w-3.5 h-3.5 ${favoritesOnly ? 'fill-current' : ''}`} />
          {t('saved')}
        </Button>
        <Button
          size="sm"
          variant={!selectedId ? 'primary' : 'ghost'}
          onPress={() => onSelect?.('')}
          className={`min-h-11 shrink-0 rounded-full px-4 py-1.5 text-xs font-medium
            ${!selectedId ? 'bg-primary text-on-primary' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}
          `}
          data-testid="category-chip-all"
        >
          {t('allCategories', { defaultValue: 'All' })}
        </Button>
        {visibleCategories.map((category: any) => {
          const isSelected = selectedId === String(category.id)

          return (
            <Button
              key={category.id}
              size="sm"
              variant={isSelected ? 'primary' : 'ghost'}
              onPress={() => onSelect?.(isSelected ? '' : String(category.id))}
              className={`min-h-11 shrink-0 rounded-full px-4 py-1.5 text-xs font-medium
                ${isSelected ? 'bg-primary text-on-primary' : 'bg-stone-100 hover:bg-stone-200 text-stone-700'}
              `}
              data-testid={`category-chip-${category.id}`}
            >
              {getCategoryName(category)}
            </Button>
          )
        })}
        {hasOverflow && (
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="min-h-11 shrink-0 rounded-full border border-stone-200 bg-stone-50 px-3 py-1.5 text-xs font-medium text-primary transition-all hover:bg-stone-100 flex items-center gap-1"
          >
            {isExpanded 
              ? t('lessCategories')
              : t('moreCategories', { count: categories.length - COLLAPSED_LIMIT })
            }
            <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
        )}
      </div>
    </div>
  )
}
